import { useState, useCallback } from 'react';
import { message } from 'antd';
import { m501Api, CandidateItem } from '../../../services/m501Api';

export interface ItemState {
  item: CandidateItem;
  selected: boolean;
}

export function useM501() {
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [queried, setQueried] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);

  /** Query candidates by adjustment-order number range */
  const handleQuery = useCallback(async (startNo: string, endNo: string) => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await m501Api.listCandidates({
        startNo: startNo.trim() || undefined,
        endNo: endNo.trim() || undefined,
      });
      setLastApiResponse(res);
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
      }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無待核准調整單');
      } else {
        message.success(`查詢到 ${mapped.length} 筆待核准調整單`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Toggle a single item's selection */
  const handleToggleItem = useCallback((adjNo: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.fm110_01 === adjNo ? { ...s, selected: checked } : s
      )
    );
  }, []);

  /** Select / deselect all items */
  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) => prev.map((s) => ({ ...s, selected: checked })));
  }, []);

  /** Update the action_taken (FM110_22) field for a single item in-place */
  const handleUpdateActionTaken = useCallback((adjNo: string, value: string) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.fm110_01 === adjNo
          ? { ...s, item: { ...s.item, fm110_22: value } }
          : s
      )
    );
  }, []);

  /** Execute batch approve for all selected items */
  const handleApprove = useCallback(async (): Promise<boolean> => {
    const selected = items.filter((s) => s.selected);
    if (selected.length === 0) {
      message.warning('請先勾選要核准的調整單');
      return false;
    }

    setApproveLoading(true);
    try {
      const res = await m501Api.approve({
        confirmed: true,
        items: selected.map((s) => ({
          adj_no: s.item.fm110_01,
          action_taken: s.item.fm110_22,
        })),
      });
      setLastApiResponse(res);

      if (res.failed === 0) {
        message.success(res.message || `核准完成，成功 ${res.success} 筆`);
      } else {
        message.warning(
          res.message || `成功 ${res.success} 筆，失敗 ${res.failed} 筆`
        );
        if (res.results) {
          res.results
            .filter((r) => !r.success)
            .forEach((r) => message.error(`${r.adj_no}: ${r.message}`));
        }
      }
      // Clear after execution (matches FoxPro ZAP VFM110 behaviour)
      setItems([]);
      setQueried(false);
      return true;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '核准執行失敗');
      return false;
    } finally {
      setApproveLoading(false);
    }
  }, [items]);

  const selectedCount = items.filter((s) => s.selected).length;

  return {
    items,
    loading,
    approveLoading,
    queried,
    lastApiResponse,
    selectedCount,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    handleUpdateActionTaken,
    handleApprove,
  };
}
